import { CheckpointsManager } from "@basthon/checkpoints";
import GUI from "./gui";

import ace from "ace-builds";
import "ace-builds/webpack-resolver";
import "ace-builds/src-noconflict/ext-language_tools";


export default class Editor {
    private _aceEditor = ace.edit("editor");
    private _gui: GUI;
    private _checkpoints: CheckpointsManager<string>;
    private _maxCheckpoints = 5;

    public constructor(gui: GUI) {
        this._gui = gui;

        /* per language checkpoints */
        const language = this._gui.language;
        this._checkpoints = new CheckpointsManager<string>(
            `console.${language}`, this._maxCheckpoints);
    }

    /**
     * Kernel getter.
     */
    public get kernel() { return this._gui.kernel; }
    public get kernelSafe() { return this._gui.kernelSafe; }

    /**
     * Set ace theme.
     */
    public async setTheme(dark: boolean) {
        const theme = dark ? "monokai" : "xcode";
        this._aceEditor.setTheme(`ace/theme/${theme}`);
    }

    /**
     * Initialize the editor's content.
     */
    public async loadScript() {
        /* set script from:
           -> query string if submited
           -> checkpoints if available
           -> default otherwise
        */
        const url = new URL(window.location.href);
        const script_key = 'script';
        const from_key = 'from';
        let script: string;
        if (url.searchParams.has(script_key)) {
            script = url.searchParams.get(script_key) || "";
            try {
                script = await this._gui.inflate(script);
            } catch (e) {
                /* backward compatibility with non compressed param */
                if (script != null) script = decodeURIComponent(script);
            }
            this.setContent(script);
        } else if (url.searchParams.has(from_key)) {
            let fileURL = url.searchParams.get(from_key);
            if (fileURL != null) fileURL = decodeURIComponent(fileURL);
            try {
                script = await GUI.xhr({
                    url: fileURL,
                    method: 'GET'
                }) as string;
            } catch (e) {
                const message = `Le chargement du script ${fileURL} a échoué.`;
                throw new ErrorEvent(message, { message: message });
            }
            this.setContent(script);
        } else {
            const approved = await this._checkpoints.lastHasTag("approved");
            if (approved != null) {
                if (approved) {
                    const value = await this._checkpoints.getLast() as string;
                    this.setContent(value);
                } else {
                    this._gui.confirm(
                        "",
                        "Il semble que Basthon ait rencontré un problème à sa dernière utilisation. Que voulez-vous faire ?",
                        "Choisir une sauvegarde",
                        () => {
                            this.selectCheckpoint();
                            this.validateBackup();
                        },
                        "Laisser le script vide",
                        () => {
                            this.setContent("");
                        }
                    );
                }
            } else if (this._gui.language === 'python3') {
                this.setContent('for i in range(10):\n    print(i)');
            } else {
                this.setContent("");
            }
        }

        // it seems scrollToRow is miss signed in .d.ts 
        this._aceEditor.scrollToRow(0);
        this._aceEditor.gotoLine(0, 0, false);
    };

    /**
     * Initialize the Ace editor.
     */
    public async init() {
        await this._checkpoints.ready();

        // ace/mode/python3 does not exists...
        const language = this._gui.language;
        const ace_language = language !== 'python3' ? language : 'python';
        this._aceEditor.session.setMode(`ace/mode/${ace_language}`);
        this._aceEditor.focus();

        this._aceEditor.setOptions({
            'enableLiveAutocompletion': true,
            'enableBasicAutocompletion': true,
            'highlightActiveLine': false,
            'highlightSelectedWord': true,
            'fontSize': '12pt',
        });

        // editor's completion

        // removing all completers and keeping track of local completer
        const localCompleter = this._aceEditor.completers[1];  // any hint to detect this dynamically?
        this._aceEditor.completers = [{
            getCompletions: (editor, session, pos, prefix, callback) => {
                // get local completions (from editor, not namespace)
                let locals: any[] = [];
                localCompleter.getCompletions(editor, session, pos, prefix,
                    function(_: any, completions: any[]) { locals = completions; });
                // recover editor content up to position
                let lines = editor.getValue().split("\n");
                lines = lines.slice(0, pos.row + 1);
                lines[lines.length - 1] = lines[lines.length - 1].slice(0, pos.column);
                const src = lines.join("\n");
                // Basthon completion
                const kernelCompletions = this.kernelSafe?.complete(src);
                if (!kernelCompletions?.length) return;
                const start = kernelCompletions[1];
                let completions = kernelCompletions[0];
                // removing prefix
                const basthon_prefix = src.slice(start, -prefix.length);
                completions = completions.map((c: string) => c.slice(basthon_prefix.length));
                // union with local completions
                const completionsSet = new Set<string>(completions);
                for (let c of locals) {
                    c = c.value;
                    if (!(completionsSet.has(c) || completionsSet.has(c + "(")))
                        completionsSet.add(c);
                }

                // rendering
                callback(null, [...completionsSet].map((value: string) => ({
                    caption: value,
                    value: value,
                    meta: "code",
                    score: 100
                })));
            }
        }];

        await this.loadScript();
    };

    /**
     * Downloading editor content as filename.
     */
    public download(filename = "script.py") {
        let code = this.getContent();
        code = code.replace(/\r\n|\r|\n/g, "\r\n"); // To retain the Line breaks.
        let blob = new Blob([code], { type: "text/plain" });
        let anchor = document.createElement("a");
        anchor.download = filename;
        anchor.href = window.URL.createObjectURL(blob);
        anchor.target = "_blank";
        anchor.style.display = "none"; // just to be safe!
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
    };

    /**
     * Set editor's content (undo selection).
     */
    public setContent(content?: string) {
        if (content != null) this._aceEditor.setValue(content);
        this._aceEditor.scrollToRow(0);
        this._aceEditor.gotoLine(0, 0, false);
    };

    /**
     * Get editor's content
     */
    public getContent() {
        return this._aceEditor.getValue();
    }

    /**
     * Opening file (async) and loading it in the editor.
     */
    public open(file: Blob | File) {
        return new Promise<void>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsText(file);
            reader.onload = (event) => {
                this.setContent(event?.target?.result as string);
                resolve();
            };
            reader.onerror = reject;
        });
    };

    /**
     * Returning the sharing link for the code in the editor.
     */
    public async sharingURL() {
        // to retain the Line breaks.
        const code = this.getContent().replace(/\r\n|\r|\n/g, "\r\n");
        const url = new URL(window.location.href);
        url.hash = "";
        url.searchParams.delete("from"); // take care of collapsing params
        let script: string;
        try {
            script = await this._gui.deflate(code);
        } catch (e) { // fallback
            script = encodeURIComponent(code).replace(/\(/g, '%28').replace(/\)/g, '%29');
        }
        url.searchParams.set('script', script);
        return url.href;
    };

    /**
     * Backup to checkpoints.
     */
    public async backup(approved = true) {
        return await this._checkpoints.push(this.getContent(), approved ? ["approved"] : []);
    };

    /**
     * Tag last backup as "approved".
     */
    public async validateBackup() {
        await this._checkpoints.tagLast("approved");
    };

    /**
     * Select a checkpoint to load in the script.
     */
    public async selectCheckpoint() {
        const times = await this._checkpoints.times();
        const choices = times?.map(
            (t, i) => ({
                text: CheckpointsManager.toHumanDate(parseInt(t, 10)),
                handler: async () => {
                    const content = await this._checkpoints.getIndex(i);
                    if (content != null) this.setContent(content);
                }
            }));
        if (choices == null) return;
        this._gui.select(
            "",
            "De quelle sauvegarde souhaitez-vous reprendre ?",
            choices,
            "Annuler",
            () => { }
        );
    };
}
